import React, { useEffect, useMemo, useState } from 'react';
import UploadModal from '../components/common/UploadModal';
import { api, fetchData, postData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { MetadataTypeInfo, MetadataTypesResponse } from '../types/api';
import { useLayout } from '../contexts/LayoutContext';
import '../styles/screens/MetadataUpload.css';

const loadMetadataTypes = async (): Promise<MetadataTypeInfo[]> => {
  const url = api(PATH_SEGMENTS.METADATA, 'types');
  const response = await fetchData<{ data: MetadataTypesResponse }>(url);
  return response?.data?.metadata_types ?? [];
};

const uploadMetadataFile = async (
  metadataKey: string,
  file: File,
  updateExisting: boolean
) => {
  const params = new URLSearchParams();
  params.set('metadata', metadataKey);
  params.set('update_existing', updateExisting ? 'true' : 'false');

  const formData = new FormData();
  formData.append('file', file);

  const url = `${api(PATH_SEGMENTS.METADATA, 'upload')}?${params.toString()}`;
  return postData<{ message?: string; data: any }>(url, formData, 'POST', 'multipart/form-data');
};

const MetadataUpload: React.FC = () => {
  const { updateHeaderProps } = useLayout();
  const [metadataTypes, setMetadataTypes] = useState<MetadataTypeInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<MetadataTypeInfo | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    updateHeaderProps({
      pageTitle: 'Metadata Uploads',
      showInteractionExId: false,
    });
  }, [updateHeaderProps]);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        setLoading(true);
        const types = await loadMetadataTypes();
        setMetadataTypes(types);
        setError(null);
      } catch (err) {
        console.error('Failed to load metadata types', err);
        setError('Failed to load metadata types.');
      } finally {
        setLoading(false);
      }
    };
    loadTypes();
  }, []);

  const handleOpenModal = (metadataType: MetadataTypeInfo) => {
    setSelectedType(metadataType);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedType(null);
  };

  const handleUpload = async (file: File, updateExisting: boolean) => {
    if (!selectedType) {
      throw new Error('No metadata type selected');
    }
    return uploadMetadataFile(selectedType.key, file, updateExisting);
  };

const modalInstructions = useMemo(() => {
  if (!selectedType) {
    return null;
  }
  return (
    <div className="metadata-modal-instructions">
      <p>
        <strong>Filename format:</strong> {selectedType.filename_format}
      </p>
      <p>
        <strong>Allowed extensions:</strong> {selectedType.allowed_extensions.join(', ')}
      </p>
      <p>
        <strong>Structure guidance:</strong> {selectedType.file_structure_hint}
      </p>
      {selectedType.required_fields && selectedType.required_fields.length > 0 && (
        <div>
          <strong>Required fields:</strong>
          <ul>
            {selectedType.required_fields.map(field => (
              <li key={field}>
                <code>{field}</code>
              </li>
            ))}
          </ul>
        </div>
      )}
      {selectedType.optional_fields && selectedType.optional_fields.length > 0 && (
        <div>
          <strong>Optional fields:</strong>
          <ul>
            {selectedType.optional_fields.map(field => (
              <li key={field}>
                <code>{field}</code>
              </li>
            ))}
          </ul>
        </div>
      )}
      {selectedType.example_snippet && (
        <div className="metadata-example-snippet">
          <strong>Example:</strong>
          <pre>
            <code>{selectedType.example_snippet}</code>
          </pre>
        </div>
      )}
    </div>
  );
}, [selectedType]);

  const handleRowKeyDown = (evt: React.KeyboardEvent<HTMLTableRowElement>, item: MetadataTypeInfo) => {
    if (evt.key === 'Enter' || evt.key === ' ') {
      evt.preventDefault();
      handleOpenModal(item);
    }
  };

  return (
    <div className="metadata-upload-page">
      {/* <div className="metadata-upload-header">
        <div>
          <h2>Metadata Uploads</h2>
          <p>Review each metadata type, then click a row to open the uploader with detailed instructions.</p>
        </div>
      </div> */}

      {error && <div className="metadata-upload-error">{error}</div>}

      <div className="metadata-table-wrapper">
        <table className="metadata-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description & File Structure</th>
              <th>Filename Format</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="metadata-table-loading">
                  Loading metadata types...
                </td>
              </tr>
            ) : metadataTypes.length === 0 ? (
              <tr>
                <td colSpan={3} className="metadata-table-empty">
                  No metadata types available.
                </td>
              </tr>
            ) : (
              metadataTypes.map((item) => (
                <tr
                  key={item.key}
                  className="metadata-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenModal(item)}
                  onKeyDown={(evt) => handleRowKeyDown(evt, item)}
                >
                  <td>
                    <div className="metadata-name">
                      <span className="metadata-key-primary">{item.key.toUpperCase()}</span>
                      <span className="metadata-label hint-text">{item.label}</span>
                    </div>
                  </td>
                  <td>
                    <p className="metadata-description">{item.description}</p>
                    <p className="metadata-hint">{item.file_structure_hint}</p>
                    {/* {item.required_fields && item.required_fields.length > 0 && (
                      <div className="metadata-field-list">
                        <strong>Required:</strong>
                        <ul>
                          {item.required_fields.map((field) => (
                            <li key={`${item.key}-required-${field}`}>
                              <code>{field}</code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {item.optional_fields && item.optional_fields.length > 0 && (
                      <div className="metadata-field-list">
                        <strong>Optional:</strong>
                        <ul>
                          {item.optional_fields.map((field) => (
                            <li key={`${item.key}-optional-${field}`}>
                              <code>{field}</code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )} */}
                    {item.example_snippet && (
                      <div className="metadata-field-list">
                        <strong>Example:</strong>
                        <pre className="metadata-inline-example">
                          <code>{item.example_snippet}</code>
                        </pre>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="metadata-filename-info">
                      <div className="metadata-filename-format">{item.filename_format}</div>
                      <div className="metadata-filename-exts">
                        Allowed: {item.allowed_extensions.join(', ')}
                      </div>
                      <button
                        className="metadata-upload-button"
                        onClick={(evt) => {
                          evt.stopPropagation();
                          handleOpenModal(item);
                        }}
                      >
                        Upload
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UploadModal
        isOpen={modalOpen && !!selectedType}
        onClose={handleCloseModal}
        onUpload={handleUpload}
        filenameBase={selectedType ? `metadata-${selectedType.key}` : 'metadata'}
        acceptedFileTypes={selectedType?.allowed_extensions}
        modalTitle={selectedType ? `Upload ${selectedType.label}` : 'Upload Metadata'}
        fileLabel={selectedType ? `Select ${selectedType.label} file` : 'Select file'}
        fileTypeDescription={
          selectedType ? `Allowed: ${selectedType.allowed_extensions.join(', ')}` : undefined
        }
        instructions={modalInstructions}
      />
    </div>
  );
};

export default MetadataUpload;

